// Shared perspective-projection helpers used by both FlatSlot and CanvasSlot.
// A perspective-projected rectangle is a trapezoid, not a parallelogram, so
// a single 3-point affine can't map the full quad — we split into triangles
// ABD and BCD and apply two affines, each clipped to its own triangle.
export function rotate3d(pt, cosX, sinX, cosY, sinY, cosZ = 1, sinZ = 0) {
    const [x, y, z] = pt;
    // Z first — spins the shape in plane while still at z=0. After Z, the
    // remaining axes are screen-aligned, so subsequent Y and X rotations
    // operate in the world frame. That makes rotationX always "lean the
    // screen-top edge away" regardless of how Z has reoriented the sheet.
    const x1 = cosZ * x - sinZ * y;
    const y1 = sinZ * x + cosZ * y;
    // Y around the world Y axis (screen vertical).
    const x2 = cosY * x1 + sinY * z;
    const z2 = -sinY * x1 + cosY * z;
    // X around the world X axis (screen horizontal) — applied last, so this
    // is the "lean back" rotation.
    const y3 = cosX * y1 - sinX * z2;
    const z3 = sinX * y1 + cosX * z2;
    return [x2, y3, z3];
}
export function project(pt, persp, cx, cy) {
    const denom = Math.max(1, persp - pt[2]);
    const s = persp / denom;
    return [cx + pt[0] * s, cy + pt[1] * s];
}
// Affine pinning srcA→pA, srcB→pB, srcD→pD. Returns null if degenerate.
export function computeAffine(sA, sB, sD, pA, pB, pD) {
    const u1 = sB[0] - sA[0];
    const v1 = sB[1] - sA[1];
    const u2 = sD[0] - sA[0];
    const v2 = sD[1] - sA[1];
    const det = u1 * v2 - u2 * v1;
    if (Math.abs(det) < 1e-9)
        return null;
    const rx1 = pB[0] - pA[0];
    const rx2 = pD[0] - pA[0];
    const ry1 = pB[1] - pA[1];
    const ry2 = pD[1] - pA[1];
    const mA = (rx1 * v2 - rx2 * v1) / det;
    const mC = (u1 * rx2 - u2 * rx1) / det;
    const mB = (ry1 * v2 - ry2 * v1) / det;
    const mD = (u1 * ry2 - u2 * ry1) / det;
    const mE = pA[0] - mA * sA[0] - mC * sA[1];
    const mF = pA[1] - mB * sA[0] - mD * sA[1];
    return [mA, mB, mC, mD, mE, mF];
}
export function matrixString(m) {
    return `matrix(${m[0].toFixed(5)},${m[1].toFixed(5)},${m[2].toFixed(5)},${m[3].toFixed(5)},${m[4].toFixed(2)},${m[5].toFixed(2)})`;
}
export function pointsString(pts) {
    return pts.map((p) => `${p[0].toFixed(3)},${p[1].toFixed(3)}`).join(" ");
}
// Project a 3D quad to screen coords + emit the two-triangle render frame.
// `vpX,vpY` is the projection center (vanishing point). For shared-perspective
// layouts the same vpX,vpY is passed to every slot; for per-slot mode each
// slot passes its own center.
// `offset` translates the rotated points before projection — needed when the
// quad rotates around its own local origin but lives off-centre relative to
// the vanishing point (i.e. shared-perspective mode for FlatSlot).
export function projectQuad(corners, src, cosX, sinX, cosY, sinY, persp, vpX, vpY, offset = [0, 0, 0], cosZ = 1, sinZ = 0) {
    const rotated = corners.map((p) => {
        const r = rotate3d(p, cosX, sinX, cosY, sinY, cosZ, sinZ);
        return [r[0] + offset[0], r[1] + offset[1], r[2] + offset[2]];
    });
    const ex = rotated[1][0] - rotated[0][0];
    const ey = rotated[1][1] - rotated[0][1];
    const fx = rotated[3][0] - rotated[0][0];
    const fy = rotated[3][1] - rotated[0][1];
    const nz = ex * fy - ey * fx;
    const proj = rotated.map((p) => project(p, persp, vpX, vpY));
    const sC = [
        src.B[0] + src.D[0] - src.A[0],
        src.B[1] + src.D[1] - src.A[1],
    ];
    const m1 = computeAffine(src.A, src.B, src.D, proj[0], proj[1], proj[3]);
    const m2 = computeAffine(src.B, sC, src.D, proj[1], proj[2], proj[3]);
    if (!m1 || !m2)
        return null;
    return {
        proj,
        polyPts: pointsString(proj),
        tri1Pts: pointsString([proj[0], proj[1], proj[3]]),
        tri2Pts: pointsString([proj[1], proj[2], proj[3]]),
        matrix1: matrixString(m1),
        matrix2: matrixString(m2),
        nz,
    };
}
// Returns the 4 projected screen-space corners of a flat slot (sheet) in
// [TL, TR, BR, BL] order, matching the cornersLocal layout used by
// projectQuad. The editor uses this for both drawing the selection overlay
// and hit-testing point-in-quad. Canvas slots use the same outer-sheet quad
// as their bounding shape — it sits in the box's z=0 plane (the back face).
export function projectedSheetQuad(slot, layout) {
    const cx = slot.x;
    const cy = slot.y;
    const w = slot.width;
    const h = slot.height;
    const persp = layout.perspective || 1400;
    const shared = layout.sharedPerspective !== false;
    const vpX = shared ? layout.perspectiveOrigin.x : cx;
    const vpY = shared ? layout.perspectiveOrigin.y : cy;
    const offX = cx - vpX;
    const offY = cy - vpY;
    const cosX = Math.cos((slot.rotationX * Math.PI) / 180);
    const sinX = Math.sin((slot.rotationX * Math.PI) / 180);
    const cosY = Math.cos((slot.rotationY * Math.PI) / 180);
    const sinY = Math.sin((slot.rotationY * Math.PI) / 180);
    const cosZ = Math.cos((slot.rotation * Math.PI) / 180);
    const sinZ = Math.sin((slot.rotation * Math.PI) / 180);
    const cornersLocal = [
        [-w / 2, -h / 2, 0],
        [w / 2, -h / 2, 0],
        [w / 2, h / 2, 0],
        [-w / 2, h / 2, 0],
    ];
    return cornersLocal.map((p) => {
        const r = rotate3d(p, cosX, sinX, cosY, sinY, cosZ, sinZ);
        return project([r[0] + offX, r[1] + offY, r[2]], persp, vpX, vpY);
    });
}
// Screen-space corners of a decoration in [TL, TR, BR, BL] order — the same
// ordering projectedSheetQuad uses, so the overlay's corner-handle cursors
// and the scale outward-vectors line up. A decoration is a flat 2D rectangle
// (no perspective, no 3D rotation): centre (x,y), half-extents from the
// scaled width/height, spun by its 2D rotation.
export function decorationQuad(d) {
    const hw = d.width / 2;
    const hh = d.height / 2;
    const rad = (d.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const local = [
        [-hw, -hh],
        [hw, -hh],
        [hw, hh],
        [-hw, hh],
    ];
    return local.map(([lx, ly]) => [
        d.x + lx * cos - ly * sin,
        d.y + lx * sin + ly * cos,
    ]);
}
// Point-in-convex-quad test. The 4 projected corners of a sheet always form
// a convex quad (rectangle under rotation + perspective stays convex), so
// the consistent-sign cross-product test works.
export function pointInQuad(p, quad) {
    if (quad.length < 4)
        return false;
    let sign = 0;
    for (let i = 0; i < 4; i++) {
        const a = quad[i];
        const b = quad[(i + 1) % 4];
        const cross = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
        if (cross !== 0) {
            const next = cross > 0 ? 1 : -1;
            if (sign === 0)
                sign = next;
            else if (sign !== next)
                return false;
        }
    }
    return true;
}
